import {useContext, useState} from "react";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {VoicemailConfigsTable} from "@/components/pages/settings/telephony/VoicemailConfigsTable";
import VoicemailConfigsForm from "@/components/pages/settings/telephony/VoicemailConfigsForm";
import {VoicemailConfigRequest, VoicemailConfigResponse} from "@/api/dto/connect";
import {ToastType} from "patient-ping-remedy/packages/toast";
import {AxiosError} from "axios";
import Helpers from "@/helpers/helpers";
import {useAlertStore} from "@/store/alert_store";

export type VoicemailConfigChangeRequest =
    | { mode: 'create', request: VoicemailConfigRequest}
    | { mode: 'edit', request: VoicemailConfigRequest, id: number}

export default function VoiceMailConfigsTab() {
  const { carecoApi } = useContext(CarecoApiContext);
  const { addAlert } = useAlertStore();
  const queryClient = useQueryClient();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<VoicemailConfigResponse | null>(null);

  const {
    data: voicemailConfigs = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['voicemailConfigs'],
    queryFn:  () => carecoApi?.getVoicemailConfigs(),
    enabled: !!carecoApi
  });

  const closeForm = () => {
    setIsFormOpen(false);
    setEditTarget(null);
    resetSaveError();
  };

  const {
    mutate: saveVoicemailConfig,
    isPending: isSaving,
    reset: resetSaveError,
  } = useMutation({
    mutationFn: async (changeRequest: VoicemailConfigChangeRequest) => {
      if (!carecoApi) return;
      if (changeRequest.mode === 'create') {
        return carecoApi?.createVoicemailConfig(changeRequest.request);
      }
      return carecoApi?.updateVoicemailConfig(changeRequest.request, changeRequest.id);
    },
    onSuccess(_data, variables) {
      void queryClient.invalidateQueries({ queryKey: ['voicemailConfigs'] });
      addAlert({
        type: ToastType.SUCCESS,
        content: variables.mode === 'create'
          ? 'Voicemail config saved successfully.'
          : 'Voicemail config updated successfully.',
      });
      setIsFormOpen(false);
      setEditTarget(null);
    },
    onError(error: AxiosError) {
      Helpers.alertOnError(`Failed to save voicemail config. ${Helpers.traceId(error)}`, error);
    },
  });

  const {
    mutate: removeVoicemailConfig,
  } = useMutation({
    mutationFn: async (id: number) => {
      if (!carecoApi) return;
      return carecoApi.deleteVoicemailConfig(id);
    },
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: ['voicemailConfigs'] });
      addAlert({
        type: ToastType.SUCCESS,
        content: 'Voicemail config deleted successfully.',
      });
    },
    onError(error: AxiosError) {
      const backendMessage = Helpers.backendErrorMessage(error);
      Helpers.alertOnError(
        backendMessage ?? `Failed to delete voicemail config. ${Helpers.traceId(error)}`,
        error,
      );
    },
  });

  const removeConfig = (id: number) => removeVoicemailConfig(id);

  return (
    <>
      <VoicemailConfigsTable
        voicemailConfigs={voicemailConfigs}
        isLoading={isLoading}
        error={error}
        onAddClick={() => {
          setIsFormOpen(true);
          setEditTarget(null);
        }}
        onEdit={(config: VoicemailConfigResponse) => {
          setIsFormOpen(true);
          setEditTarget(config);
        }}
        onRemove={removeConfig}/>

      <VoicemailConfigsForm
        isOpen={isFormOpen}
        close={closeForm}
        onSave={(request) => saveVoicemailConfig(request)}
        editTarget={editTarget}
        isSaving={isSaving}
      />
    </>
  );
}
